import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { ProfileStore, useProfileStore } from '@/stores/profile-store';
import { Swiper } from 'swiper/types';
import { useToast } from '@/composables/useToast';
import { i18n } from '@/plugins/i18n';
import { useAuth } from '@/composables/useAuth';
import { AuthStore, useAuthStore } from '@/stores/auth-store';
import { notify } from '@kyvg/vue3-notification';
import { PlaceLocation } from '@/interfaces/elastic/elastic.interface';
import { IPlaceLocation } from '@/interfaces/auth/auth-store.interface';

export const useProfile = () => {
  const profileStore: ProfileStore = useProfileStore();
  const authStore: AuthStore = useAuthStore();

  const {
    profileForm,
    hasProfile,
    documentSelected,
    documentForm,
    toggleEditProfile,
    toggleChangePassword,
    toggleChangeEmail,
    toggleRecoveryPassword,
    toggleDocument,
    documents,
    languageSelected,
    pushNotifications,
    isProfileCompleted,
  } = storeToRefs(profileStore);

  const { user } = storeToRefs(authStore);

  const { showToast } = useToast();
  const { changeEmail, changePhoto } = useAuth();

  const loading = ref(false);
  const changeEmailSuccess = ref(false);
  const updatePhotoSuccess = ref(false);
  const changePasswordSuccess = ref(false);
  const recoveryPasswordSuccess = ref(false);
  const swiperChangeEmail = ref<Swiper>();
  const swiperChangePassword = ref<Swiper>();
  const swiperRecoveryPassword = ref<Swiper>();

  const handleUpdatePhoto = async (base64: string) => {
    try {
      loading.value = true;
      const response = await profileStore.updatePhoto(base64);

      if (response) {
        updatePhotoSuccess.value = true;
        changePhoto(response?.photo ?? '');
        await showToast(i18n.global.t('profile.photo.photoChangedSuccessfully'));
        return response;
      }
    } catch (error: unknown) {
      updatePhotoSuccess.value = false;
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleChangeEmail = async (email: string) => {
    try {
      loading.value = true;
      const response = await profileStore.sendCode(email);

      if (response) {
        changeEmailSuccess.value = true;
        await showToast(response);
        return response;
      }
      await showToast(response);
    } catch (error: unknown) {
      changeEmailSuccess.value = false;
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleVerifyCode = async (email: string, code: string) => {
    try {
      loading.value = true;
      const response = await profileStore.verifyCode(email, code);

      if (response) {
        changeEmailSuccess.value = true;
        changeEmail(email);
        await showToast(i18n.global.t('profile.email.emailChangedSuccessfully'));
        return response;
      }
    } catch (error: unknown) {
      changeEmailSuccess.value = false;
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    try {
      loading.value = true;
      const response = await profileStore.changePassword(currentPassword, newPassword);

      if (response) {
        changePasswordSuccess.value = true;
        await showToast(i18n.global.t('profile.password.passwordChangedSuccessfully'));
        return response;
      }
    } catch (error: unknown) {
      changePasswordSuccess.value = false;
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleChangeCurrentPassword = async (newPassword: string) => {
    try {
      const response = await profileStore.changeCurrentPassword(newPassword);

      if (response) {
        await showToast(i18n.global.t('profile.password.passwordChangedSuccessfully'));
        return response;
      }
    } catch (error: unknown) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  const handleRecoveryPassword = async (email: string) => {
    try {
      loading.value = true;
      const msg = await profileStore.recoveryPassword(email);

      if (msg) {
        recoveryPasswordSuccess.value = true;
        await showToast(msg);
        return msg;
      }
    } catch (error: unknown) {
      recoveryPasswordSuccess.value = false;
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };
  const handleResetPassword = async (password: string, token: string) => {
    try {
      loading.value = true;
      const response = await profileStore.resetPassword(password, token);

      if (response) {
        recoveryPasswordSuccess.value = true;
        await showToast(i18n.global.t('profile.recovery.recoveryPasswordSuccessfully'));
        return response;
      }
    } catch (error: unknown) {
      recoveryPasswordSuccess.value = false;
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleGetProfile = async () => {
    try {
      loading.value = true;
      const response = await profileStore.getProfile();

      if (response) {
        profileForm.value = {
          name: response.name ?? undefined,
          lastName: response.lastName ?? undefined,
          documentType: response.documentType ?? undefined,
          documentId: response.documentId ?? undefined,
          phoneNumber: response.phoneNumber ?? undefined,
          prefixPhone: response.prefixPhone ?? undefined,
          genre: response.genre ?? undefined,
          birthday: response.birthday ?? undefined,
          userType: response.userType ?? undefined,
          NationalityId: response.Nationality?.id ?? 0,
          location: response.Location?.address ?? undefined,
        };
        return response;
      } else {
        throw new Error('Error');
      }
    } catch (error: unknown) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleGetProfileLocation = async () => {
    try {
      loading.value = true;
      const response = await profileStore.getProfileLocation();

      if (response) {
        profileForm.value.location = response.Location?.fullAddress ?? undefined;
        return response;
      } else {
        throw new Error('Error');
      }
    } catch (error: unknown) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handlePutProfile = async () => {
    try {
      loading.value = true;
      const response = await profileStore.updateProfile();
      const responseLocation = await profileStore.updateProfileLocation(
        profileForm.value.Location as PlaceLocation,
      );

      if (response && responseLocation) {
        user.value = {
          ...user.value,
          ...response,
          Location: responseLocation.Location as IPlaceLocation,
        };

        profileForm.value = {
          name: response.name ?? undefined,
          lastName: response.lastName ?? undefined,
          documentType: response.documentType ?? undefined,
          documentId: response.documentId ?? undefined,
          phoneNumber: response.phoneNumber ?? undefined,
          prefixPhone: response.prefixPhone ?? undefined,
          genre: response.genre ?? undefined,
          birthday: response.birthday ?? undefined,
          userType: response.userType ?? undefined,
          NationalityId: response.Nationality?.id ?? 0,
          location: responseLocation.Location?.fullAddress ?? undefined,
        };
        await showToast(i18n.global.t('profile.profileUpdatedSuccessfully'));
        return response;
      } else {
        throw new Error('Error');
      }
    } catch (error: unknown) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleGetSetting = async () => {
    try {
      loading.value = true;
      const response = await profileStore.getSetting();

      if (response) {
        languageSelected.value = response.language;
        pushNotifications.value = response.pushNotifications;
        return response;
      } else {
        throw new Error('Error');
      }
    } catch (error: unknown) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handlePutSetting = async (language: string, push: boolean) => {
    try {
      loading.value = true;
      const { data, msg } = await profileStore.updateSetting(language, push);
      if (data) {
        languageSelected.value = data.Settings.language;
        pushNotifications.value = data.Settings.pushNotifications;
        i18n.global.locale.value = languageSelected.value;
        await showToast(msg as string);
        return data;
      } else {
        throw new Error('Error');
      }
    } catch (error: unknown) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      loading.value = false;
    }
  };

  const handleGetDocuments = async () => {
    try {
      await profileStore.getDocuments();
    } catch (error) {
      const errorMsg = error?.toString() ?? 'Unknown error!';
      notify({
        type: 'error',
        title: errorMsg,
      });
    }
  };

  const handleRemoveAccount = async (password: string) => {
    try {
      const { data } = await profileStore.removeAccount(password);
      if (data) {
        return data;
      } else {
        throw new Error('Error');
      }
    } catch (error: unknown) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  return {
    profileForm,
    hasProfile,
    documentForm,
    documentSelected,
    toggleEditProfile,
    loading,
    swiperChangePassword,
    swiperChangeEmail,
    swiperRecoveryPassword,
    toggleChangePassword,
    toggleChangeEmail,
    toggleRecoveryPassword,
    toggleDocument,
    changeEmailSuccess,
    changePasswordSuccess,
    recoveryPasswordSuccess,
    updatePhotoSuccess,
    documents,
    languageSelected,
    pushNotifications,
    isProfileCompleted,
    handleChangeEmail,
    handleUpdatePhoto,
    handleVerifyCode,
    handleChangePassword,
    handleRecoveryPassword,
    handleResetPassword,
    handleGetProfile,
    handlePutProfile,
    handleGetDocuments,
    handleGetSetting,
    handlePutSetting,
    handleGetProfileLocation,
    handleRemoveAccount,
    handleChangeCurrentPassword,
  };
};
